import type { FC } from "react";

import type { UsageContextEfficiencySummary } from "../../types/usage";

import { formatUsd } from "../../utils/currency";
import { DataPanel } from "../common/DataPanel";
import { MetricCard } from "../common/MetricCard";

export interface UsageContextEfficiencyPanelProps {
  summary: UsageContextEfficiencySummary;
}

function formatRate(part: number, total: number): string {
  if (total <= 0) {
    return "—";
  }
  return `${Math.round((part / total) * 100)}%`;
}

function formatInteger(value: number): string {
  return value.toLocaleString();
}

export const UsageContextEfficiencyPanel: FC<
  UsageContextEfficiencyPanelProps
> = ({ summary }) => (
  <DataPanel
    title="Context Efficiency"
    subtitle="Repeated file reads avoided re-sending file content; cost savings are repriced against the next LLM call in the selected window."
    icon={
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    }
    iconColor="amber"
    className="hover:shadow-none"
  >
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label="Context Saved"
        value={`${formatInteger(summary.avoidedPromptTokens ?? summary.savedTokens)} tokens`}
        subValue={
          (summary.pricedHitCount ?? 0) > 0
            ? `~${formatUsd(summary.savedUsdEstimate)} est. prompt cost avoided`
            : "Approx prompt tokens avoided"
        }
        variant="emerald"
      />
      <MetricCard
        label="Read Cache Hit Rate"
        value={formatRate(summary.cacheHitCount, summary.readCount)}
        subValue={`${formatInteger(summary.cacheHitCount)} / ${formatInteger(summary.readCount)} repeated reads`}
        variant="amber"
      />
      <MetricCard
        label="Exact / Covered"
        value={`${formatInteger(summary.exactMatchCount)} / ${formatInteger(summary.coveredMatchCount)}`}
        subValue="Identical hits vs wider prior reads"
        variant="indigo"
      />
      <MetricCard
        label="Total Read Tokens"
        value={formatInteger(summary.returnedTokens)}
        subValue="Tokens across all file reads"
        variant="violet"
      />
    </div>
  </DataPanel>
);
